import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Headline, Text, Spinner,
  Button, Card, Avatar, Select, Snackbar, FormItem,
} from '@vkontakte/vkui';
import { Icon28CopyOutline, Icon28CheckCircleOutline, Icon28CancelOutline, Icon28DeleteOutline } from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../api/client';
import ShareInviteButton from '../../ui/ShareInviteButton';
import { buildInviteLink } from '../../utils/joinCode';
import type { AccessLevel, MemberStatus, ModuleName, WeddingMember } from '../../types';

const MODULE_LABELS: Record<ModuleName, string> = {
  guests: 'Гости',
  tasks: 'Задачи',
  budget: 'Бюджет',
  vendors: 'Подрядчики',
  schedule: 'Расписание',
  settings: 'Настройки',
  members: 'Участники',
  messages: 'Сообщения',
};

const MODULES = Object.keys(MODULE_LABELS) as ModuleName[];

const LEVEL_OPTIONS = [
  { label: 'Нет', value: 'deny' },
  { label: 'Чтение', value: 'read' },
  { label: 'Запись', value: 'write' },
];

const STATUS_LABELS: Record<MemberStatus, string> = {
  pending: 'Ожидает',
  accepted: 'Принят',
  declined: 'Отклонён',
  revoked: 'Отозван',
};

const ROLE_LABELS: Record<string, string> = {
  owner: 'Владелец',
  partner: 'Партнёр',
  member: 'Участник',
};

function memberName(m: WeddingMember): string {
  const name = [m.user_first_name, m.user_last_name].filter(Boolean).join(' ');
  return name || `Участник ${m.user_id.slice(0, 6)}`;
}

export default function MembersPage() {
  const navigate = useNavigate();
  const {
    currentWedding, members, isLoading,
    loadMembers, updateMemberStatus, removeMember,
    updateMemberPermissions, regenerateInviteCode,
    transferOwner, acceptTransferOwner,
  } = useWeddingStore();
  const { user } = useAuthStore();

  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmTransferId, setConfirmTransferId] = useState<string | null>(null);
  const [pendingTransfer, setPendingTransfer] = useState(false);

  const myMember = useMemo(
    () => members.find((m) => m.user_id === user?.id),
    [members, user?.id],
  );
  const isOwner = myMember?.role === 'owner';

  useEffect(() => {
    if (currentWedding) {
      loadMembers().catch(() => setSnackbar('Не удалось загрузить участников'));
      api.get<{ pending: boolean }>(`weddings/${currentWedding.id}/members/ownership-transfer/pending`)
        .then((r) => setPendingTransfer(!!r.pending))
        .catch(() => setPendingTransfer(false));
    }
  }, [currentWedding?.id]);

  const inviteLink = currentWedding
    ? buildInviteLink(currentWedding.invite_code)
    : '';

  async function copyInvite() {
    const text = `Код: ${currentWedding?.invite_code}\nСсылка: ${inviteLink}`;
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar('Ссылка скопирована');
    } catch {
      setSnackbar(`Код: ${currentWedding?.invite_code}`);
    }
  }

  async function setStatus(m: WeddingMember, status: MemberStatus) {
    try {
      await updateMemberStatus(m.id, status);
      setSnackbar(status === 'accepted' ? 'Участник принят' : status === 'declined' ? 'Заявка отклонена' : 'Доступ отозван');
      loadMembers().catch(() => {});
    } catch {
      setSnackbar('Не удалось изменить статус');
    }
  }

  async function savePermissions(m: WeddingMember, module: ModuleName, level: AccessLevel) {
    try {
      await updateMemberPermissions(m.id, [{ module, level }]);
      setSnackbar('Права обновлены');
    } catch {
      setSnackbar('Не удалось обновить права');
    }
  }

  async function handleRemove(m: WeddingMember) {
    try {
      await removeMember(m.id);
      setSnackbar('Участник исключён');
      setConfirmDeleteId(null);
    } catch {
      setSnackbar('Не удалось исключить участника');
    }
  }

  async function handleTransfer(m: WeddingMember) {
    try {
      await transferOwner(m.id);
      setConfirmTransferId(null);
      setSnackbar('Попросите участника подтвердить передачу');
    } catch {
      setSnackbar('Не удалось начать передачу');
    }
  }

  async function handleAcceptTransfer(m: WeddingMember) {
    try {
      await acceptTransferOwner(m.id);
      setSnackbar('Вы стали владельцем');
      setPendingTransfer(false);
      loadMembers().catch(() => {});
    } catch {
      setSnackbar('Не удалось принять владение');
    }
  }

  function levelOf(m: WeddingMember, module: ModuleName): AccessLevel {
    return m.permissions?.find((p) => p.module === module)?.level ?? 'deny';
  }

  if (!currentWedding) {
    return (
      <Panel>
        <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>Участники</PanelHeader>
        <Div>
          <Text>Сначала выберите свадьбу</Text>
          <Button style={{ marginTop: 12 }} onClick={() => navigate('/')}>На главную</Button>
        </Div>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Участники
      </PanelHeader>

      {/* Invite */}
      <Group header={<Text>Приглашение</Text>}>
        <Card style={{ margin: 12, padding: 14 }}>
          <Headline level="2">Код: {currentWedding.invite_code}</Headline>
          <Text style={{ marginTop: 6, color: 'var(--vkui--color_text_secondary)', fontSize: 13, wordBreak: 'break-all' }}>
            {inviteLink}
          </Text>
          <Div style={{ padding: 0, marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ShareInviteButton
              invite={{
                code: currentWedding.invite_code,
                link: inviteLink,
                title: currentWedding.name,
              }}
            />
            <Button size="s" mode="secondary" before={<Icon28CopyOutline />} onClick={copyInvite}>
              Скопировать
            </Button>
            {isOwner && (
              <Button
                size="s"
                mode="outline"
                onClick={async () => {
                  try {
                    await regenerateInviteCode();
                    setSnackbar('Новый код создан');
                  } catch {
                    setSnackbar('Не удалось создать код');
                  }
                }}
              >
                Новый код
              </Button>
            )}
          </Div>
          <Text style={{ marginTop: 10, fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
            Владелец подтверждает заявку после ввода кода.
          </Text>
        </Card>
      </Group>

      {isLoading && members.length === 0 ? (
        <Div><Spinner /></Div>
      ) : (
        <Group header={<Text>Список ({members.length})</Text>}>
          {members.length === 0 && (
            <Div><Text>Пока никого нет. Поделитесь кодом приглашения.</Text></Div>
          )}
          {members.map((m) => {
            const expanded = expandedId === m.id;
            const canManage = isOwner && m.role !== 'owner';
            return (
              <Card key={m.id} style={{ margin: '8px 12px', padding: 12 }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: canManage ? 'pointer' : 'default' }}
                  onClick={() => canManage && setExpandedId(expanded ? null : m.id)}
                >
                  <Avatar size={40} src={m.user_avatar_url} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Headline level="2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {memberName(m)}
                      {m.user_id === user?.id ? ' (вы)' : ''}
                    </Headline>
                    <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
                      {ROLE_LABELS[m.role] || m.role} · {STATUS_LABELS[m.status]}
                    </Text>
                  </div>
                </div>

                {/* Owner actions for pending / accepted */}
                {isOwner && m.status === 'pending' && m.role !== 'owner' && (
                  <Div style={{ padding: '8px 0 0', display: 'flex', gap: 8 }}>
                    <Button size="s" mode="primary" before={<Icon28CheckCircleOutline />} onClick={() => setStatus(m, 'accepted')}>
                      Принять
                    </Button>
                    <Button size="s" mode="secondary" before={<Icon28CancelOutline />} onClick={() => setStatus(m, 'declined')}>
                      Отклонить
                    </Button>
                  </Div>
                )}

                {/* Self: accept ownership transfer */}
                {m.user_id === user?.id && m.role !== 'owner' && pendingTransfer && (
                  <Div style={{ padding: '8px 0 0' }}>
                    <Button size="s" mode="primary" onClick={() => handleAcceptTransfer(m)}>
                      Принять владение
                    </Button>
                  </Div>
                )}

                {/* Expanded: ACL + actions */}
                {expanded && canManage && (
                  <Div style={{ padding: '8px 0 0' }}>
                    <Text style={{ marginBottom: 8, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                      Права доступа
                    </Text>
                    {MODULES.map((mod) => (
                      <FormItem key={mod} top={MODULE_LABELS[mod]} style={{ padding: '4px 0' }}>
                        <Select
                          options={LEVEL_OPTIONS}
                          value={levelOf(m, mod)}
                          onChange={(e) => savePermissions(m, mod, e.currentTarget.value as AccessLevel)}
                        />
                      </FormItem>
                    ))}

                    <Div style={{ padding: '12px 0 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {m.status === 'accepted' && (
                        <Button size="s" mode="outline" onClick={() => setConfirmTransferId(m.id)}>
                          Передать владение
                        </Button>
                      )}
                      <Button size="s" mode="outline" before={<Icon28DeleteOutline />} onClick={() => setConfirmDeleteId(m.id)}>
                        Исключить
                      </Button>
                    </Div>

                    {confirmDeleteId === m.id && (
                      <Div style={{ padding: '8px 0 0' }}>
                        <Text style={{ marginBottom: 8 }}>Исключить {memberName(m)}?</Text>
                        <Button size="s" mode="outline" onClick={() => handleRemove(m)}>Да, исключить</Button>
                        <Button size="s" mode="secondary" style={{ marginLeft: 8 }} onClick={() => setConfirmDeleteId(null)}>Отмена</Button>
                      </Div>
                    )}
                    {confirmTransferId === m.id && (
                      <Div style={{ padding: '8px 0 0' }}>
                        <Text style={{ marginBottom: 8 }}>Передать владение {memberName(m)}? Потребуется его подтверждение.</Text>
                        <Button size="s" mode="primary" onClick={() => handleTransfer(m)}>Начать передачу</Button>
                        <Button size="s" mode="secondary" style={{ marginLeft: 8 }} onClick={() => setConfirmTransferId(null)}>Отмена</Button>
                      </Div>
                    )}
                  </Div>
                )}
              </Card>
            );
          })}
        </Group>
      )}

      {snackbar && (
        <Snackbar onClose={() => setSnackbar(null)} onClosed={() => setSnackbar(null)}>{snackbar}</Snackbar>
      )}
    </Panel>
  );
}
